/**
 * volumeProfile — derive POC / VAH / VAL / HVNs / LVNs from OHLCV bars.
 *
 * Volume Profile is one of the futures-native level frameworks (per the
 * NexusFi + OrderFlow Labs primers cited in the Top-Down design doc §7).
 * Coach previously captured ZERO volume-profile context — every reference
 * level was geometric (PDH/PDL/Golden Crayon/IB) or AVWAP-anchored. This
 * module adds the volume-weighted distribution lens.
 *
 * Algorithm (uniform-distribution proxy per design doc §7.1):
 *   1. For each bar with finite low/high/volume > 0, distribute its
 *      volume uniformly across the integer-tick price levels in
 *      [low, high]. We don't have tick-level data; uniform is the
 *      industry-standard approximation when only OHLCV is available.
 *   2. Aggregate to build the volumeByPrice map across all bars.
 *   3. POC = price level with maximum volume.
 *   4. VA = expand outward from POC by adding the higher-volume adjacent
 *      level on each step until cumulative volume reaches the target
 *      (default 70% of total).
 *   5. VAH = top of VA range; VAL = bottom.
 *   6. HVN = local-maxima prices in volumeByPrice (peaks); top N by volume.
 *   7. LVN = local-minima prices in volumeByPrice (troughs); bottom N by
 *      volume (these are the "thin" areas price tends to traverse fast).
 *
 * **Limitation:** bar-level VP is an APPROXIMATION (intra-bar volume
 * distribution is unknown). Industry-standard caveat. When tick-level
 * data becomes available (e.g., Polygon.io secondary bar source per
 * the roadmap), this module can be upgraded to use actual tick prints.
 *
 * Pure function. Caller is responsible for slicing bars to the desired
 * window (RTH-only, full session, etc.).
 */

export const DEFAULT_OPTS = Object.freeze({
  tickSize: 0.25,        // MNQ/NQ/ES/MES default; pass instrument-specific for accuracy
  vaPercent: 0.70,       // standard Value Area threshold (70% of total volume)
  hvnLvnCount: 3,        // top-N high/low-volume nodes to emit
});

/** Tick-aligned price rounding via integer-tick arithmetic — avoids
 *  floating-point accumulation drift over many bars. */
const roundToTickIdx = (price, tickSize) => Math.round(Number(price) / tickSize);

/**
 * Build the volume profile from a bars array.
 *
 * @param {Array<{low:number, high:number, volume:number}>} bars
 * @param {Object} [opts]
 * @returns {{
 *   poc: number,
 *   vah: number,
 *   val: number,
 *   totalVolume: number,
 *   hvnClusters: number[],
 *   lvnClusters: number[],
 * } | null}  Null when no bars produce any volume (empty input, all-NaN, etc.).
 */
export const buildVolumeProfile = (bars, opts = {}) => {
  if (!Array.isArray(bars) || bars.length === 0) return null;

  const tickSize = Number(opts.tickSize ?? DEFAULT_OPTS.tickSize);
  const vaPercent = Number(opts.vaPercent ?? DEFAULT_OPTS.vaPercent);
  const hvnLvnCount = Math.max(1, Math.floor(Number(opts.hvnLvnCount ?? DEFAULT_OPTS.hvnLvnCount)));
  if (!Number.isFinite(tickSize) || tickSize <= 0) return null;
  if (!Number.isFinite(vaPercent) || vaPercent <= 0 || vaPercent > 1) return null;

  // 1. + 2. Distribute each bar's volume across its low-to-high tick range.
  // Use integer tick indices to avoid float accumulation issues.
  const volumeByTickIdx = new Map();  // tickIdx → cumulative volume
  for (const bar of bars) {
    const lo = Number(bar?.low);
    const hi = Number(bar?.high);
    const v = Number(bar?.volume);
    if (!Number.isFinite(lo) || !Number.isFinite(hi) || !Number.isFinite(v) || v <= 0) continue;
    if (hi < lo) continue;  // malformed bar
    const startIdx = roundToTickIdx(lo, tickSize);
    const endIdx = roundToTickIdx(hi, tickSize);
    const numLevels = endIdx - startIdx + 1;
    const perLevel = v / Math.max(1, numLevels);
    for (let idx = startIdx; idx <= endIdx; idx++) {
      volumeByTickIdx.set(idx, (volumeByTickIdx.get(idx) || 0) + perLevel);
    }
  }

  if (volumeByTickIdx.size === 0) return null;

  // 3. POC.
  let pocIdx = null;
  let pocVolume = 0;
  for (const [idx, vol] of volumeByTickIdx) {
    if (vol > pocVolume) { pocIdx = idx; pocVolume = vol; }
  }

  // 4. + 5. Value Area expansion from POC.
  // Build a sorted ascending list of tick indices for adjacency walking.
  const sortedIndices = [...volumeByTickIdx.keys()].sort((a, b) => a - b);
  const totalVolume = [...volumeByTickIdx.values()].reduce((s, v) => s + v, 0);
  const targetVolume = totalVolume * vaPercent;

  // Walk outward from POC; at each step take the higher-volume adjacent
  // level. Track current high/low boundaries within sortedIndices.
  let posInSorted = sortedIndices.indexOf(pocIdx);
  let hiBound = posInSorted;
  let loBound = posInSorted;
  let accumulated = pocVolume;

  while (accumulated < targetVolume && (loBound > 0 || hiBound < sortedIndices.length - 1)) {
    const canGoUp = hiBound < sortedIndices.length - 1;
    const canGoDown = loBound > 0;
    const aboveVol = canGoUp ? volumeByTickIdx.get(sortedIndices[hiBound + 1]) : -1;
    const belowVol = canGoDown ? volumeByTickIdx.get(sortedIndices[loBound - 1]) : -1;
    if (canGoUp && aboveVol >= belowVol) {
      hiBound++;
      accumulated += aboveVol;
    } else if (canGoDown) {
      loBound--;
      accumulated += belowVol;
    } else {
      break;  // shouldn't happen given the while guard, but defensive
    }
  }

  const vahIdx = sortedIndices[hiBound];
  const valIdx = sortedIndices[loBound];

  // 6. + 7. HVN/LVN local extrema. Walk sortedIndices comparing each
  // level's volume to its immediate neighbors. Skip endpoints (no
  // neighbor on one side → can't be a clean local extremum).
  const hvnCandidates = [];
  const lvnCandidates = [];
  for (let i = 1; i < sortedIndices.length - 1; i++) {
    const idx = sortedIndices[i];
    const v = volumeByTickIdx.get(idx);
    const vPrev = volumeByTickIdx.get(sortedIndices[i - 1]);
    const vNext = volumeByTickIdx.get(sortedIndices[i + 1]);
    if (v > vPrev && v > vNext) hvnCandidates.push({ idx, v });
    if (v < vPrev && v < vNext) lvnCandidates.push({ idx, v });
  }
  hvnCandidates.sort((a, b) => b.v - a.v);
  lvnCandidates.sort((a, b) => a.v - b.v);

  return {
    poc: pocIdx * tickSize,
    vah: vahIdx * tickSize,
    val: valIdx * tickSize,
    totalVolume,
    hvnClusters: hvnCandidates.slice(0, hvnLvnCount).map(x => x.idx * tickSize),
    lvnClusters: lvnCandidates.slice(0, hvnLvnCount).map(x => x.idx * tickSize),
  };
};
